import React from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';

interface ProductCount {
  name: string;
  sku: string;
  count: number;
}

interface DailyPurchaseSummary {
  date: string;
  products: ProductCount[];
}

interface Props {
  days: DailyPurchaseSummary[];
  startDate: string;
  endDate: string;
  selectedNames: string[];
  colors: string[];
}

function getAmbrCount(day: DailyPurchaseSummary): number {
  const p = day.products.find(p => p.sku.toUpperCase() === 'AMBR');
  return p ? p.count : 0;
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysBetween(start: string, end: string): number {
  const ms = new Date(end + 'T00:00:00').getTime() - new Date(start + 'T00:00:00').getTime();
  return Math.round(ms / 86_400_000) + 1;
}

export function AmbrSpotlightCard({ days, startDate, endDate, selectedNames, colors }: Props) {
  const today = new Date().toISOString().slice(0, 10);
  const todayEntry = days.find(d => d.date === today);
  const todayCount = todayEntry ? getAmbrCount(todayEntry) : 0;

  const periodTotal = days.reduce((sum, d) => sum + getAmbrCount(d), 0);
  const numDays = daysBetween(startDate, endDate);
  const periodLabel = `${formatShortDate(startDate)} – ${formatShortDate(endDate)} · ${numDays} days`;

  // Period totals for each selected product
  const selectedTotals = selectedNames.map((name, i) => {
    const total = days.reduce((sum, d) => {
      const p = d.products.find(pr => pr.name === name);
      return sum + (p ? p.count : 0);
    }, 0);
    return { name, total, color: colors[i % colors.length] };
  });

  // Build 7-day trend data (last 7 calendar days, fill missing with 0)
  const trendData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const date = d.toISOString().slice(0, 10);
    const entry = days.find(day => day.date === date);
    return { date, count: entry ? getAmbrCount(entry) : 0 };
  });

  return (
    <div
      data-testid="ambr-spotlight-card"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '1.25rem 1.5rem',
        minWidth: '260px',
        flex: '0 0 auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Associate Membership
      </div>

      {/* Today count */}
      <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1a2332', lineHeight: 1.2, marginTop: '0.4rem' }}>
        {todayCount}
      </div>
      <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '1rem' }}>
        purchased today
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #f3f4f6', marginBottom: '0.75rem' }} />

      {/* Period label */}
      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
        {periodLabel}
      </div>

      {/* Selected products summary table */}
      {selectedTotals.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' }}>
          {selectedTotals.map(({ name, total, color }) => (
            <div
              key={name}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                <span style={{
                  fontSize: '0.8rem', color: '#374151',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {name}
                </span>
              </div>
              <span style={{ fontSize: '1rem', fontWeight: 700, color, flexShrink: 0 }}>
                {total.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>Period total</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2563eb' }}>{periodTotal}</span>
        </div>
      )}

      {/* Sparkline */}
      <div style={{ height: 44 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData}>
            <Line
              type="monotone"
              dataKey="count"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
            />
            <Tooltip contentStyle={{ fontSize: '0.75rem' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.2rem' }}>7-day trend</div>
    </div>
  );
}
